import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createSettingsTemplate, validateSettingsTemplateSource } from '../api/alertApi';
import { asText, buildPayload, emptyTemplate, parseJson } from '../utils/templateForm';
import TemplateWizardFields from './TemplateWizardFields';

export default function TemplateCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState(emptyTemplate);
  const [activeStep, setActiveStep] = useState('basics');
  const [sourceTestDeviceId, setSourceTestDeviceId] = useState('');
  const [sourceValidationResult, setSourceValidationResult] = useState(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      if (!payload.name) throw new Error('Template name is required');
      if (payload.source_type === 'http' && !payload.data_source_url) {
        throw new Error('Data source URL is required');
      }
      if (payload.source_type === 'mongodb' && (!payload.mongodb_uri || !payload.mongodb_database || !payload.mongodb_collection)) {
        throw new Error('MongoDB connection string, database, and collection are required');
      }
      return createSettingsTemplate(payload);
    },
    onSuccess: (result) => {
      toast.success('Template created');
      onCreated(result.data);
    },
    onError: (err) => toast.error(err.message || err.response?.data?.error || 'Create failed'),
  });

  const validateSourceMutation = useMutation({
    mutationFn: () => validateSettingsTemplateSource({
      source_type: form.source_type,
      data_source_url: asText(form.data_source_url).trim(),
      method: form.method,
      headers: parseJson(form.headersText, 'Headers'),
      query_params: parseJson(form.queryParamsText, 'Query params'),
      request_body: parseJson(form.requestBodyText, 'Request body'),
      mongodb_uri: asText(form.mongodb_uri).trim(),
      mongodb_database: asText(form.mongodb_database).trim(),
      mongodb_collection: asText(form.mongodb_collection).trim(),
      mongodb_filter: parseJson(form.mongodb_filterText, 'MongoDB filter'),
      mongodb_sort_field: asText(form.mongodb_sort_field).trim() || '_id',
      variable_mapping: {},
      sample_device_id: asText(sourceTestDeviceId).trim(),
    }),
    onSuccess: (result) => {
      setSourceValidationResult(result);
      toast.success(result.success ? 'Source check completed' : 'Source check returned an error');
    },
    onError: (err) => toast.error(err.message || err.response?.data?.error || 'Source check failed'),
  });

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateVariable(index, key, value) {
    setForm((current) => ({
      ...current,
      variableRows: current.variableRows.map((row, idx) => idx === index ? { ...row, [key]: value } : row),
    }));
  }

  function addVariable() {
    setForm((current) => ({
      ...current,
      variableRows: [...current.variableRows, { key: '', path: '', format: 'none', timestampFormat: 'YYYY-MM-DD HH:mm:ss', timezone: '' }],
    }));
  }

  function removeVariable(index) {
    setForm((current) => ({
      ...current,
      variableRows: current.variableRows.length === 1
        ? [{ key: '', path: '', format: 'none', timestampFormat: 'YYYY-MM-DD HH:mm:ss', timezone: '' }]
        : current.variableRows.filter((_, idx) => idx !== index),
    }));
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Create New Template</div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
              Define the data source, variable mapping, and messages. Devices are attached afterwards
              in the Devices module.
            </div>
          </div>
          <button style={closeButton} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={modalBody}>
          <TemplateWizardFields
            form={form}
            updateField={updateField}
            updateVariable={updateVariable}
            addVariable={addVariable}
            removeVariable={removeVariable}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            sourceTestDeviceId={sourceTestDeviceId}
            setSourceTestDeviceId={setSourceTestDeviceId}
            sourceValidationResult={sourceValidationResult}
            onValidateSource={() => validateSourceMutation.mutate()}
            validatingSource={validateSourceMutation.isPending}
            onSave={() => createMutation.mutate()}
            saving={createMutation.isPending}
            saveLabel="Create Template"
          />
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '40px 20px',
  overflowY: 'auto',
  zIndex: 1000,
};

const modal = {
  background: '#f8fbff',
  borderRadius: 24,
  width: 960,
  maxWidth: '100%',
  boxShadow: '0 30px 70px rgba(15, 23, 42, 0.3)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100vh - 80px)',
};

const modalHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'start',
  gap: 16,
  padding: '22px 24px',
  borderBottom: '1px solid #dbe4f0',
};

const modalBody = {
  padding: 24,
  overflowY: 'auto',
  display: 'grid',
  gap: 18,
};

const closeButton = {
  border: 'none',
  background: '#eef2f7',
  color: '#334155',
  width: 32,
  height: 32,
  borderRadius: '50%',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '32px',
  padding: 0,
};
